import { useEffect, useRef, useState } from "react";
import type { Bootstrap, Spend } from "../../shared/types";
import { api, type NewSpend } from "../lib/api";
import { formatMoney, formatPeriod, formatDayMonth, parseMoney, todayISO } from "../lib/format";

export interface SpendPrefill {
  where?: string;
  amount?: string;
  occurredOn?: string;
}

// Load a photo, shrink it to maxDim on the long edge, return a JPEG data URL.
// Keeps the upload small and fast for the AI to read.
async function downscaleImage(file: File, maxDim: number): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}
import StatCard from "../components/StatCard";
import Modal from "../components/Modal";
import Select from "../components/Select";
import BalanceModal from "../components/BalanceModal";
import ConfirmModal from "../components/ConfirmModal";
import { EditIcon, TrashIcon } from "../components/icons";

// Explain "Funds remaining": the balance you entered, minus only what has left
// the account since that snapshot (day-to-day spends + bills/plans ticked paid
// afterwards). Makes it obvious the figure is derived, not a stale balance.
function fundsRemainingSub(funds: Bootstrap["funds"]): string {
  if (funds.combinedMinor == null) return "Set balance to track";
  // New pay cycle, balance not yet updated: we've carried the salary on as an
  // estimate — say so and nudge an update rather than showing a stale breakdown.
  if (funds.carriedSalaryMinor > 0) {
    const on = funds.asOf ? ` from ${formatDayMonth(funds.asOf)}` : "";
    return `Est. — last balance${on} + ${formatMoney(funds.carriedSalaryMinor)} salary · update to confirm`;
  }
  const balance = funds.combinedMinor;
  const remaining = funds.fundsRemainingMinor ?? balance;
  const since = balance - remaining; // >0 = money has gone out since the snapshot
  const on = funds.asOf ? ` set ${formatDayMonth(funds.asOf)}` : "";
  if (since > 0) return `${formatMoney(balance)}${on}, less ${formatMoney(since)} since · update if changed`;
  if (since < 0) return `${formatMoney(balance)}${on}, plus ${formatMoney(-since)} in since`;
  return `${formatMoney(balance)}${on} · update if changed`;
}

export default function Spending({
  data,
  reload,
}: {
  data: Bootstrap | null;
  reload: () => Promise<void>;
}) {
  const [spends, setSpends] = useState<Spend[]>([]);
  const [logKind, setLogKind] = useState<"out" | "in" | null>(null);
  const [editing, setEditing] = useState<Spend | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Spend | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<SpendPrefill | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [showBalance, setShowBalance] = useState(false);
  // List filters (client-side, over the loaded period).
  const [fSearch, setFSearch] = useState("");
  const [fPerson, setFPerson] = useState("all");
  const [fCategory, setFCategory] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const period = data?.summary.period;

  async function onReceiptFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setScanning(true);
    setScanError(null);
    try {
      const image = await downscaleImage(file, 1100);
      const r = await api.scanReceipt(image);
      setPrefill({
        where: r.where ?? "",
        amount: r.amountMinor != null ? (r.amountMinor / 100).toFixed(2) : "",
        occurredOn: r.date ?? todayISO(),
      });
      setLogKind("out");
    } catch (err) {
      setScanError((err as Error).message || "Couldn't read that receipt");
      setPrefill(null);
      setLogKind("out"); // still open the form so it can be entered by hand
    } finally {
      setScanning(false);
    }
  }

  const refresh = async () => {
    if (!period) return;
    const res = await api.listSpends(period);
    setSpends(res.spends);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (!data || !period) return <div className="state">Loading…</div>;
  const { funds } = data;
  const outSpends = spends.filter((s) => s.kind === "out");
  const spendCount = outSpends.length;

  // Who spent what this period. Sums the same 'out' spends as "Spent this month",
  // grouped by payer, so the per-person tiles add up to that headline figure.
  const perPerson = data.household.members.map((m) => {
    const mine = outSpends.filter((s) => s.paidBy === m.id);
    return { member: m, total: mine.reduce((t, s) => t + s.amountMinor, 0), count: mine.length };
  });
  const unassigned = outSpends.filter((s) => s.paidBy == null);
  const unassignedTotal = unassigned.reduce((t, s) => t + s.amountMinor, 0);

  // Apply the filter bar to the transaction list (spends + money-in).
  const q = fSearch.trim().toLowerCase();
  const filtered = spends.filter((s) => {
    if (fPerson !== "all") {
      if (fPerson === "none" ? s.paidBy != null : String(s.paidBy ?? "") !== fPerson) return false;
    }
    if (fCategory !== "all" && String(s.categoryId ?? "") !== fCategory) return false;
    if (fFrom && s.occurredOn < fFrom) return false;
    if (fTo && s.occurredOn > fTo) return false;
    if (q && !`${s.where} ${s.note ?? ""}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const filtersActive = fPerson !== "all" || fCategory !== "all" || Boolean(fFrom) || Boolean(fTo) || Boolean(q);
  const filteredOutTotal = filtered.filter((s) => s.kind === "out").reduce((t, s) => t + s.amountMinor, 0);
  const filteredInTotal = filtered.filter((s) => s.kind === "in").reduce((t, s) => t + s.amountMinor, 0);
  function clearFilters() {
    setFSearch(""); setFPerson("all"); setFCategory("all"); setFFrom(""); setFTo("");
  }

  async function doDelete(spend: Spend) {
    setConfirmDelete(null);
    setActionError(null);
    setSpends((s) => s.filter((x) => x.id !== spend.id)); // optimistic
    try {
      await api.deleteSpend(spend.id);
      await refresh();
      await reload();
    } catch (e) {
      // Put it back and say why — otherwise a failed delete silently reappears.
      await refresh();
      setActionError(`Couldn't delete "${spend.where}": ${(e as Error).message}`);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <p className="page__eyebrow">Spending</p>
          <h1 className="page__title">Day-to-day</h1>
          <p className="page__lede">
            Spends and one-off money in {formatPeriod(period)}, against your funds remaining.
          </p>
        </div>
        <div className="month-actions">
          <button className="btn btn--ghost" onClick={() => setShowBalance(true)}>Update balance</button>
          <button className="btn btn--ghost" onClick={() => setLogKind("in")}>+ Money in</button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onReceiptFile} />
          <button className="btn btn--ghost" onClick={() => fileRef.current?.click()} disabled={scanning}>
            {scanning ? "Reading…" : "📷 Scan receipt"}
          </button>
          <button className="btn btn--primary" onClick={() => { setPrefill(null); setLogKind("out"); }}>+ Log spend</button>
        </div>
      </div>

      <section className="stat-grid">
        <StatCard
          label="Funds remaining"
          value={funds.fundsRemainingMinor != null ? formatMoney(funds.fundsRemainingMinor) : "—"}
          sub={fundsRemainingSub(funds)}
          accent
        />
        <StatCard label="Spent this month" value={formatMoney(funds.spentThisMonthMinor)} sub={`${spendCount} spend${spendCount === 1 ? "" : "s"}`} />
        <StatCard label="Money in" value={formatMoney(funds.incomingThisMonthMinor)} sub="One-off payments in" tone={funds.incomingThisMonthMinor > 0 ? "positive" : undefined} />
        <StatCard label="Spent since update" value={formatMoney(funds.spentSinceUpdateMinor)} sub="Deducted from balance" />
      </section>

      {/* Who spent what this period — one tile per person. */}
      <section className="stat-grid person-spend-grid">
        {perPerson.map(({ member, total, count }) => (
          <StatCard
            key={member.id}
            label={`${member.name} spent`}
            value={formatMoney(total)}
            sub={`${count} spend${count === 1 ? "" : "s"} · ${formatPeriod(period)}`}
            icon={<span className="avatar avatar--xs" style={{ background: member.colour }}>{member.initial}</span>}
          />
        ))}
        {unassignedTotal > 0 && (
          <StatCard
            label="Unassigned"
            value={formatMoney(unassignedTotal)}
            sub={`${unassigned.length} spend${unassigned.length === 1 ? "" : "s"} · no payer set`}
          />
        )}
      </section>

      {actionError && <div className="state state--error" role="alert">{actionError}</div>}

      <div className="bills-toolbar">
        <input
          className="input bills-search"
          placeholder="Search name or note…"
          value={fSearch}
          onChange={(e) => setFSearch(e.target.value)}
        />
        <div className="rec-filter">
          <Select
            value={fPerson}
            onChange={setFPerson}
            ariaLabel="Filter by who spent"
            options={[
              { value: "all", label: "Anyone" },
              ...data.household.members.map((m) => ({ value: String(m.id), label: m.name })),
              { value: "none", label: "Unassigned" },
            ]}
          />
        </div>
        <div className="rec-filter">
          <Select
            value={fCategory}
            onChange={setFCategory}
            ariaLabel="Filter by category"
            options={[{ value: "all", label: "All categories" }, ...data.categories.map((c) => ({ value: String(c.id), label: c.name }))]}
          />
        </div>
        <input className="input spend-date" type="date" value={fFrom} max={fTo || undefined} onChange={(e) => setFFrom(e.target.value)} aria-label="From date" title="From date" />
        <input className="input spend-date" type="date" value={fTo} min={fFrom || undefined} onChange={(e) => setFTo(e.target.value)} aria-label="To date" title="To date" />
        {filtersActive && <button className="btn btn--ghost btn--sm" onClick={clearFilters}>Clear</button>}
      </div>

      {filtersActive && (
        <p className="spend-filter-sum">Showing {filtered.length} of {spends.length}</p>
      )}

      <div className="bill-list">
        <div className="bill-list__head spend-head">
          <span>Date</span>
          <span>Where</span>
          <span>Amount</span>
        </div>
        {spends.length === 0 ? (
          <div className="empty">Nothing logged this month yet.</div>
        ) : filtered.length === 0 && (
          <div className="empty">No spends match these filters.</div>
        )}
        {filtered.map((s) => (
          <div className="spend-row" key={s.id}>
            <span className="spend-row__date">{formatDayMonth(s.occurredOn)}</span>
            <div className="spend-row__main">
              <span className="bill-row__name">{s.where}</span>
              <span className="bill-row__tags">
                {s.kind === "in" ? (
                  <span className="type-tag type-tag--in">Money in</span>
                ) : (
                  <>
                    {s.categoryName && (
                      <span className="chip">
                        <span className="chip__dot" style={{ background: s.categoryColour ?? "#98A2B3" }} />
                        {s.categoryName}
                      </span>
                    )}
                    {s.offBalance && <span className="type-tag type-tag--variable">Off balance</span>}
                  </>
                )}
              </span>
              {s.note && <span className="spend-row__note">{s.note}</span>}
            </div>
            <div className="bill-row__right">
              {s.paidByInitial && (
                <span className="avatar" style={{ background: s.paidByColour ?? "#98A2B3" }}>{s.paidByInitial}</span>
              )}
              <span className={"bill-row__amount" + (s.kind === "in" ? " amount--in" : "")}>
                {s.kind === "in" ? "+" : ""}{formatMoney(s.amountMinor)}
              </span>
              <button className="icon-btn" onClick={() => { setActionError(null); setEditing(s); }} aria-label="Edit" title="Edit"><EditIcon /></button>
              <button className="icon-btn" onClick={() => setConfirmDelete(s)} aria-label="Delete" title="Delete"><TrashIcon /></button>
            </div>
          </div>
        ))}
        {filtered.length > 0 && (
          <div className="table-total">
            <span className="table-total__label">{filtersActive ? "Total (filtered)" : "Total spent"}</span>
            <span className="table-total__value">
              {formatMoney(filteredOutTotal)}
              {filteredInTotal > 0 && <> · <span className="amount--in">+{formatMoney(filteredInTotal)} in</span></>}
            </span>
          </div>
        )}
      </div>

      {(logKind || editing) && (
        <LogModal
          data={data}
          kind={editing ? editing.kind : logKind!}
          editing={editing}
          prefill={!editing && logKind === "out" ? prefill : null}
          scanError={!editing && logKind === "out" ? scanError : null}
          onClose={() => { setLogKind(null); setEditing(null); setPrefill(null); setScanError(null); }}
          onSaved={async () => {
            setLogKind(null);
            setEditing(null);
            setPrefill(null);
            setScanError(null);
            await refresh();
            await reload();
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Delete this spend?"
          message={`"${confirmDelete.where}" (${formatMoney(confirmDelete.amountMinor)}) will be removed and your balance adjusted back. This can't be undone.`}
          confirmLabel="Delete spend"
          danger
          onConfirm={() => doDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
      {showBalance && (
        <BalanceModal data={data} onClose={() => setShowBalance(false)} onSaved={async () => { setShowBalance(false); await reload(); }} />
      )}
    </div>
  );
}

function LogModal({
  data,
  kind,
  editing,
  prefill,
  scanError,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  kind: "out" | "in";
  editing?: Spend | null;
  prefill?: SpendPrefill | null;
  scanError?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isIn = kind === "in";
  const [where, setWhere] = useState(editing?.where ?? prefill?.where ?? "");
  const [amount, setAmount] = useState(editing ? (editing.amountMinor / 100).toFixed(2) : prefill?.amount ?? "");
  const [occurredOn, setOccurredOn] = useState(editing?.occurredOn ?? prefill?.occurredOn ?? todayISO());
  const [categoryId, setCategoryId] = useState<string>(editing?.categoryId ? String(editing.categoryId) : "");
  const [paidBy, setPaidBy] = useState<string>(editing?.paidBy ? String(editing.paidBy) : "");
  const [offBalance, setOffBalance] = useState(editing ? editing.offBalance : true);
  const [note, setNote] = useState(editing?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const minor = parseMoney(amount);
    if (!where.trim()) return setError(isIn ? "What's it for?" : "Where did you spend?");
    if (minor == null || minor <= 0) return setError("Enter a valid amount");
    const spend: NewSpend = {
      occurredOn,
      where: where.trim(),
      amountMinor: minor,
      categoryId: isIn ? null : categoryId ? Number(categoryId) : null,
      paidBy: paidBy ? Number(paidBy) : null,
      offBalance: isIn ? true : offBalance,
      kind,
      note: note.trim() || null,
      budgetItemId: editing ? editing.budgetItemId : undefined,
    };
    setSaving(true);
    try {
      if (editing) await api.updateSpend(editing.id, spend);
      else await api.createSpend(spend);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const title = editing
    ? (isIn ? "Edit money in" : "Edit spend")
    : (isIn ? "Add money in" : "Log spend");

  return (
    <Modal title={title} onClose={onClose}>
      <div className="form">
        {scanError ? (
          <p className="field__hint">{scanError} — please enter it below.</p>
        ) : prefill ? (
          <p className="field__hint">Read from your receipt — check it's right before saving.</p>
        ) : null}
        <label className="field">
          <span className="field__label">{isIn ? "What for" : "Where"}</span>
          <input className="input" value={where} onChange={(e) => setWhere(e.target.value)} placeholder={isIn ? "Refund, gift, sale…" : "Tesco"} autoFocus />
        </label>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Amount (£)</span>
            <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={isIn ? "50.00" : "12.40"} />
          </label>
          <label className="field">
            <span className="field__label">Date</span>
            <input className="input" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </label>
        </div>
        <div className="form__row">
          {!isIn && (
            <div className="field">
              <span className="field__label">Category</span>
              <Select
                value={categoryId}
                onChange={setCategoryId}
                placeholder="—"
                ariaLabel="Category"
                options={[{ value: "", label: "—" }, ...data.categories.map((c) => ({ value: String(c.id), label: c.name }))]}
              />
            </div>
          )}
          <div className="field">
            <span className="field__label">{isIn ? "Into whose account" : "Paid by"}</span>
            <Select
              value={paidBy}
              onChange={setPaidBy}
              placeholder="—"
              ariaLabel={isIn ? "Into whose account" : "Paid by"}
              options={[{ value: "", label: "—" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
            />
          </div>
        </div>
        <label className="field">
          <span className="field__label">Description <span className="field__optional">(optional)</span></span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. weekly big shop, birthday present for Liam…" />
        </label>
        {!isIn && (
          <label className="field field--check">
            <input type="checkbox" checked={offBalance} onChange={(e) => setOffBalance(e.target.checked)} />
            <span>Deduct from balance (off-balance spend)</span>
          </label>
        )}
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : isIn ? "Add money in" : "Save spend"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
